// src/contexts/SwipeQueueContext.js
// Durable outbox for swipe writes. A caller does not advance its UI until the
// promise returned by enqueueSwipe confirms that the server accepted the write.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import matchingService from "../services/matchingService";
import { messageService } from "../services/messageService";
import {
  extractErrorCode,
  extractErrorMessage,
} from "../utils/httpErrorEnvelope";

const SwipeQueueContext = createContext({
  enqueueSwipe: async () => ({ success: false }),
  flushQueue: async () => true,
  retryFailed: async () => true,
  failedCount: 0,
});

// Keep the existing key so interrupted writes from older builds are recovered.
const STORAGE_KEY = "rxrooster_swipe_queue_v1";

const PROVIDER_INTERVIEW_ACTION = "provider-interview-request";
const PROVIDER_CONNECT_ACTION = "provider-connect";
const PROVIDER_INBOUND_RESPONSE_ACTION = "provider-inbound-response";
const PROVIDER_INBOUND_REPLY_ACTION = "provider-inbound-reply";
const isProviderInterestAction = (item) =>
  item?.type === PROVIDER_INTERVIEW_ACTION ||
  item?.type === PROVIDER_CONNECT_ACTION ||
  item?.type === PROVIDER_INBOUND_RESPONSE_ACTION ||
  item?.type === PROVIDER_INBOUND_REPLY_ACTION;

// A 4xx answer to the provider's interview request means the server read the
// request and refused it, so sending the identical request again can never
// succeed. Those are terminal: the row leaves the durable queue and the provider
// is told. A network error, a 5xx, a timeout, a rate limit and an expired
// session say nothing about the request itself, so they keep their retry.
const RETRYABLE_CLIENT_ERROR_STATUSES = new Set([401, 403, 408, 425, 429]);
// 403 is retryable unless the app itself answered it: a proxy or the platform
// can return a bare or HTML 403 (Azure's stopped-app page), which says nothing
// about the request and clears once the app is back.
const APP_FORBIDDEN_CODES = new Set(["PRACTICING_PROVIDER_REQUIRED"]);
// The backend answers 409 slot_in_past on this endpoint only when EVERY
// proposed time has passed (a request with at least one future slot is accepted
// with the past ones dropped).
const SLOT_IN_PAST_MESSAGE =
  "All of your proposed times have passed. Pick new times and send again.";
// The row is dropped as unretryable, so this must not invite a retry.
const INTERVIEW_REQUEST_FALLBACK_MESSAGE =
  "Your interview request could not be sent.";

const httpStatusOf = (error) => {
  const status = Number(error?.status ?? error?.response?.status);
  return Number.isFinite(status) ? status : null;
};

// True when the body is the app's own exception envelope for this status (the
// AllExceptionsFilter shape), as opposed to an HTML page or an empty body.
const isAppEnvelope = (error, status) => {
  const data = error?.response?.data;
  return (
    Boolean(data) &&
    typeof data === "object" &&
    data.success === false &&
    Number(data.statusCode) === status
  );
};

const isTerminalRefusal = (item, error) => {
  if (item?.type !== PROVIDER_INTERVIEW_ACTION) return false;
  const status = httpStatusOf(error);
  if (status === null || status < 400 || status >= 500) return false;
  if (status === 403) {
    return (
      isAppEnvelope(error, 403) ||
      APP_FORBIDDEN_CODES.has(extractErrorCode(error))
    );
  }
  return !RETRYABLE_CLIENT_ERROR_STATUSES.has(status);
};

const makeId = () =>
  `swipe-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const stableJson = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

const derivedActionId = (base, suffix) =>
  `${String(base).slice(0, Math.max(0, 128 - suffix.length))}${suffix}`;

const targetKey = (item) => {
  if (item.type === "update-direction") {
    return [item.type, item.swipeId || ""].join(":");
  }
  if (isProviderInterestAction(item)) {
    const payload = item.payload || {};
    // Interview and connect are two choices for the same relationship. Share
    // one target key so a late first request cannot overtake a newer choice.
    return [
      "provider-interest-action",
      payload.sourceInterestSwipeId || payload.listingId || "",
    ].join(":");
  }
  return [item.type, item.listingId || "", item.providerProfileId || ""].join(
    ":",
  );
};

const dedupeKey = (item) =>
  isProviderInterestAction(item)
    ? `${targetKey(item)}:${item.type}:${stableJson(item.payload || {})}`
    : `${targetKey(item)}:${item.direction || ""}`;

const normalizeItem = (item) => {
  const id = item.id || makeId();
  return {
    ...item,
    id,
    clientActionId: item.clientActionId || id,
    attempts: Number.isFinite(item.attempts) ? item.attempts : 0,
    status: item.status === "sending" ? "failed" : item.status || "failed",
    queuedAt: item.queuedAt || item.timestamp || Date.now(),
  };
};

function loadInitialQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((item) => item && item.type).map(normalizeItem)
      : [];
  } catch (_) {
    return [];
  }
}

export const SwipeQueueProvider = ({ children }) => {
  const initialQueueRef = useRef(null);
  if (initialQueueRef.current === null) {
    initialQueueRef.current = loadInitialQueue();
  }

  const [queue, setQueue] = useState(initialQueueRef.current);
  const queueRef = useRef(initialQueueRef.current);
  const inFlightRef = useRef(new Map());
  const targetFlightRef = useRef(new Map());

  const updateQueue = useCallback((updater) => {
    const next =
      typeof updater === "function" ? updater(queueRef.current) : updater;
    queueRef.current = next;
    try {
      if (next.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
    } catch (_) {
      // The in-memory outbox still works when storage is unavailable.
    }
    setQueue(next);
    return next;
  }, []);

  useEffect(() => {
    try {
      if (queue.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
      }
    } catch (_) {
      // The in-memory outbox still works when storage is unavailable.
    }
  }, [queue]);

  const processSwipeItem = useCallback(async (item) => {
    if (item.type === "provider") {
      const result = await matchingService.providerSwipe(
        item.listingId,
        item.direction,
        {
          clientActionId: item.clientActionId,
          ...(item.fitAcknowledged === true ? { fitAcknowledged: true } : {}),
        },
      );
      if (result == null) {
        throw new Error("The server did not acknowledge this swipe");
      }

      window.dispatchEvent(
        new CustomEvent("rxrooster:providerSwipeResult", {
          detail: {
            swipeId: result?._id,
            listingId: item.listingId,
            direction: item.direction,
            isExternalListing: result?.isExternalListing || false,
            isMatch: result?.isMatch || false,
            status: result?.status,
          },
        }),
      );

      if (item.direction === "right" && result?.isMatch) {
        window.dispatchEvent(
          new CustomEvent("rxrooster:providerMatch", {
            detail: { matchId: result._id, listingId: item.listingId },
          }),
        );
      }

      return result;
    }

    if (item.type === "group") {
      const result = await matchingService.groupSwipe(
        item.providerProfileId,
        item.direction,
        item.listingId,
        {
          clientActionId: item.clientActionId,
          ...(item.fitAcknowledged === true ? { fitAcknowledged: true } : {}),
        },
      );
      if (result == null) {
        throw new Error("The server did not acknowledge this swipe");
      }
      return result;
    }

    if (item.type === "update-direction") {
      const result = await matchingService.updateSwipeDirection(
        item.swipeId,
        item.direction,
        { clientActionId: item.clientActionId },
      );
      if (result == null) {
        throw new Error("The server did not acknowledge this reversal");
      }
      return result;
    }

    if (item.type === PROVIDER_INTERVIEW_ACTION) {
      const result = await matchingService.expressInterestWithInterviewRequest({
        ...item.payload,
        clientActionId: item.clientActionId,
      });
      if (result == null) {
        throw new Error(
          "The server did not acknowledge this interview request",
        );
      }
      return result;
    }

    if (item.type === PROVIDER_CONNECT_ACTION) {
      const payload = item.payload || {};
      const result = payload.sourceInterestSwipeId
        ? await matchingService.respondToInboundInterest(
            payload.sourceInterestSwipeId,
            "right",
            {
              openingQuestions: payload.openingQuestions || [],
              clientActionId: item.clientActionId,
              ...(payload.fitAcknowledged === true
                ? { fitAcknowledged: true }
                : {}),
            },
          )
        : await matchingService.connectWithoutInterview({
            listingId: payload.listingId,
            openingQuestions: payload.openingQuestions || [],
            clientActionId: item.clientActionId,
            ...(payload.fitAcknowledged === true
              ? { fitAcknowledged: true }
              : {}),
          });
      if (result == null) {
        throw new Error("The server did not acknowledge this connection");
      }
      return result;
    }

    if (item.type === PROVIDER_INBOUND_RESPONSE_ACTION) {
      const payload = item.payload || {};
      const result = await matchingService.respondToInboundInterest(
        payload.sourceInterestSwipeId,
        payload.direction,
        {
          ...(payload.direction === "right"
            ? { openingQuestions: payload.openingQuestions || [] }
            : {}),
          clientActionId: item.clientActionId,
          ...(payload.direction === "right" && payload.fitAcknowledged === true
            ? { fitAcknowledged: true }
            : {}),
        },
      );
      if (result == null) {
        throw new Error("The server did not acknowledge this response");
      }
      return result;
    }

    if (item.type === PROVIDER_INBOUND_REPLY_ACTION) {
      const payload = item.payload || {};
      const consentResult = await matchingService.respondToInboundInterest(
        payload.sourceInterestSwipeId,
        "right",
        {
          openingQuestions: payload.openingQuestions || [],
          clientActionId: derivedActionId(item.clientActionId, ":connect"),
        },
      );
      const swipeId =
        consentResult?.swipeId ||
        consentResult?._id ||
        consentResult?.swipe?._id ||
        null;
      if (!swipeId) {
        throw new Error("The server did not acknowledge this connection");
      }

      const message = await messageService.sendMessage(
        swipeId,
        payload.message,
        null,
        {
          clientActionId: derivedActionId(item.clientActionId, ":message"),
        },
      );
      if (message == null) {
        throw new Error("The server did not acknowledge this reply");
      }
      return { ...consentResult, swipeId, message };
    }

    throw new Error(`Unsupported swipe type: ${item.type}`);
  }, []);

  const attemptSwipe = useCallback(
    (rawItem) => {
      const item = normalizeItem(rawItem);
      const key = dedupeKey(item);
      const target = targetKey(item);
      const existingRequest = inFlightRef.current.get(key);
      if (existingRequest) return existingRequest;

      // Different decisions for the same card must reach the server in user
      // order. Sending them concurrently lets a slower, older request win.
      const previousTargetRequest = targetFlightRef.current.get(target);

      // Persist before sending. If the tab closes mid-request, the next mount
      // sees this as a failed/interrupted write instead of silently losing it.
      updateQueue((current) => {
        const withoutTarget = current.filter(
          (queued) => targetKey(queued) !== target,
        );
        return [
          ...withoutTarget,
          { ...item, status: previousTargetRequest ? "queued" : "sending" },
        ];
      });

      let request;
      request = (async () => {
        if (previousTargetRequest) {
          await previousTargetRequest;
          updateQueue((current) =>
            current.map((queued) =>
              queued.id === item.id ? { ...queued, status: "sending" } : queued,
            ),
          );
        }

        try {
          const result = await processSwipeItem(item);
          updateQueue((current) =>
            current.filter((queued) => queued.id !== item.id),
          );
          window.dispatchEvent(
            new CustomEvent("rxrooster:swipeQueueAcknowledged", {
              detail: { item, result },
            }),
          );
          return { success: true, item, result };
        } catch (error) {
          if (isTerminalRefusal(item, error)) {
            const code = extractErrorCode(error);
            const userMessage =
              code === "slot_in_past" ? SLOT_IN_PAST_MESSAGE : undefined;
            const rejected = {
              ...item,
              attempts: item.attempts + 1,
              status: "rejected",
              lastError: error?.message || "Action was refused",
              lastAttemptAt: Date.now(),
            };
            // Drop the row by id so it is never sent again, on reload, on
            // reconnect, or by a manual retry.
            updateQueue((current) =>
              current.filter((queued) => queued.id !== item.id),
            );
            return {
              success: false,
              terminal: true,
              item: rejected,
              error,
              code,
              ...(userMessage ? { userMessage } : {}),
            };
          }
          const failed = {
            ...item,
            attempts: item.attempts + 1,
            status: "failed",
            lastError: error?.message || "Action could not be saved",
            lastAttemptAt: Date.now(),
          };
          // If a newer decision replaced this item while it was running, do
          // not resurrect the stale failure over the newer durable intent.
          updateQueue((current) =>
            current.map((queued) => (queued.id === item.id ? failed : queued)),
          );
          return { success: false, item: failed, error };
        } finally {
          if (inFlightRef.current.get(key) === request) {
            inFlightRef.current.delete(key);
          }
          if (targetFlightRef.current.get(target) === request) {
            targetFlightRef.current.delete(target);
          }
        }
      })();

      inFlightRef.current.set(key, request);
      targetFlightRef.current.set(target, request);
      return request;
    },
    [processSwipeItem, updateQueue],
  );

  const enqueueSwipe = useCallback(
    (item) => {
      const normalized = normalizeItem(item || {});
      if (isProviderInterestAction(normalized)) {
        const payload = normalized.payload || {};
        const hasValidPayload =
          Boolean(payload.listingId) &&
          (normalized.type !== PROVIDER_INTERVIEW_ACTION ||
            (Array.isArray(payload.slots) && payload.slots.length > 0)) &&
          (normalized.type !== PROVIDER_INBOUND_RESPONSE_ACTION ||
            (Boolean(payload.sourceInterestSwipeId) &&
              ["right", "left"].includes(payload.direction))) &&
          (normalized.type !== PROVIDER_INBOUND_REPLY_ACTION ||
            (Boolean(payload.sourceInterestSwipeId) &&
              typeof payload.message === "string" &&
              Boolean(payload.message.trim())));
        if (!hasValidPayload) {
          return Promise.resolve({
            success: false,
            item: normalized,
            error: new Error(
              "Provider interest action is missing required data",
            ),
          });
        }
        const existing = queueRef.current.find(
          (queued) => dedupeKey(queued) === dedupeKey(normalized),
        );
        return attemptSwipe(
          existing
            ? {
                ...normalized,
                id: existing.id,
                clientActionId: existing.clientActionId || existing.id,
                attempts: existing.attempts,
                queuedAt: existing.queuedAt,
              }
            : normalized,
        );
      }

      const hasTarget =
        normalized?.type === "update-direction"
          ? Boolean(normalized?.swipeId)
          : Boolean(normalized?.listingId);
      if (!normalized?.type || !hasTarget || !normalized?.direction) {
        return Promise.resolve({
          success: false,
          item: normalized,
          error: new Error("Swipe is missing required data"),
        });
      }

      const existing = queueRef.current.find(
        (queued) => dedupeKey(queued) === dedupeKey(normalized),
      );
      return attemptSwipe(
        existing
          ? {
              ...normalized,
              id: existing.id,
              clientActionId: existing.clientActionId || existing.id,
              attempts: existing.attempts,
              queuedAt: existing.queuedAt,
            }
          : normalized,
      );
    },
    [attemptSwipe],
  );

  const flushQueue = useCallback(async () => {
    const pending = [...queueRef.current];
    if (pending.length === 0) return true;

    // A row already in flight belongs to a caller that is awaiting its result
    // (the visible form), which shows the refusal itself.
    const awaitedByCaller = new Set(
      pending
        .filter((item) => inFlightRef.current.has(dedupeKey(item)))
        .map((item) => item.id),
    );
    const results = await Promise.all(pending.map(attemptSwipe));
    // Nobody is watching a background recovery, so tell the provider when the
    // server refuses a recovered interview request for good.
    results.forEach((result) => {
      if (!result.terminal || awaitedByCaller.has(result.item.id)) return;
      // The server's own message when it sent one, else a plain statement.
      toast.error(
        result.userMessage ||
          extractErrorMessage(result.error, INTERVIEW_REQUEST_FALLBACK_MESSAGE),
        { toastId: `swipe-queue-rejected-${result.item.id}` },
      );
    });
    return results.every((result) => result.success);
  }, [attemptSwipe]);

  const retryFailed = flushQueue;
  const failedCount = queue.filter((item) => item.status === "failed").length;

  // Recover interrupted writes on the next page load and whenever the browser
  // comes back online. Failed rows remain in localStorage until a server
  // acknowledgement removes them, so a refresh can never silently discard a
  // recruiter swipe or an Undo.
  useEffect(() => {
    const retry = () => {
      void flushQueue();
    };
    retry();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [flushQueue]);

  return (
    <SwipeQueueContext.Provider
      value={{ enqueueSwipe, flushQueue, retryFailed, failedCount }}
    >
      {children}
    </SwipeQueueContext.Provider>
  );
};

export const useSwipeQueue = () => useContext(SwipeQueueContext);
